import { convexQuery } from "@convex-dev/react-query";
import BottomSheet, {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { useQuery } from "@tanstack/react-query";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { remapProps } from "react-native-css-interop";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { useSheet } from "../shared/sheet-provider";
import { Tag } from "../ui/tag";
import { api } from "../utils/convex-api";
import { contactSupport } from "../utils/helpers";
import { SheetIds } from "../utils/sheet-ids";

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type LessonPriceListSheetPayload = void;

export type LessonPriceListSheetResult = void;

const KEY_STAGE_LABELS: Record<number, string> = {
  0: "Foundation Stage",
  1: "KS1/KS2",
  2: "KS1/KS2",
  3: "KS3",
  4: "KS4",
  5: "KS5",
};

function LessonPriceSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss } = useSheet<
    LessonPriceListSheetPayload,
    LessonPriceListSheetResult
  >(SheetIds.LESSON_PRICE_LIST);
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const [activeTab, setActiveTab] = useState<"ksLevel" | "mentorship">(
    "ksLevel",
  );

  const { data: products } = useQuery(
    convexQuery(api.parent.buyCredits.queries.getTutoringProducts, {}),
  );

  const pricingData = useMemo(() => {
    if (!products) return [];

    const groupedByLabel = products.reduce(
      (acc, product) => {
        const label = KEY_STAGE_LABELS[product.keyStage ?? 0] ?? "Unknown";
        const totalCredits =
          product.tutorHourlyCredits + product.platformHourlyCredits;

        if (!acc[label] || totalCredits > acc[label].totalCredits) {
          acc[label] = { label, totalCredits };
        }
        return acc;
      },
      {} as Record<string, { label: string; totalCredits: number }>,
    );

    const order = ["Foundation Stage", "KS1/KS2", "KS3", "KS4", "KS5"];

    return order
      .filter((label) => groupedByLabel[label])
      .map((label) => groupedByLabel[label]);
  }, [products]);

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register],
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss],
  );

  const onClose = useCallback(async () => {
    close();
  }, [close]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose],
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetView style={{ backgroundColor: "#FCF9F5" }}>
        <View className="bg-teachLoop-creamBase rounded-t-[20]">
          <View className="h-6" />

          <View className="mx-5">
            <View className="flex-row gap-1 px-2">
              <Pressable
                className="flex-1"
                onPress={() => setActiveTab("ksLevel")}
              >
                <Tag
                  size="md"
                  className={`flex-1 ${activeTab === "ksLevel" ? "" : "bg-transparent"}`}
                >
                  <Tag.Text size="md">KS Level</Tag.Text>
                </Tag>
              </Pressable>
              <Pressable
                className="flex-1"
                onPress={() => setActiveTab("mentorship")}
              >
                <Tag
                  size="md"
                  className={`flex-1 ${activeTab === "mentorship" ? "" : "bg-transparent"}`}
                >
                  <Tag.Text size="md">Mentorship</Tag.Text>
                </Tag>
              </Pressable>
            </View>
            {/* KS Level */}
            {activeTab === "ksLevel" && (
              <View>
                <View className="h-[30px]" />
                <View className="px-2">
                  <Text className="text-base text-primary-0 font-geologica-regular font-normal leading-[1.5]">
                    Number of credits required per hour of tutoring
                  </Text>
                </View>
                <View className="h-[14px]" />
                <View className="p-3 bg-teachLoop-creamLight">
                  <View>
                    <View className="flex-row justify-between">
                      <Text className="text-center text-sm font-geologica-regular font-normal leading-[1.5] text-gray-600 pt-3 pb-2 flex-1">
                        Level
                      </Text>
                      <Text className="text-center text-sm font-geologica-regular font-normal leading-[1.5] text-gray-600 pt-3 pb-2 flex-1">
                        Credits/hr
                      </Text>
                      <Text className="text-center text-sm font-geologica-regular font-normal leading-[1.5] text-gray-600 pt-3 pb-2 flex-1">
                        Price
                      </Text>
                    </View>
                    <View
                      className="h-[1px] bg-gray-200"
                      style={{ marginHorizontal: -12 }}
                    />
                  </View>
                  {pricingData.map((item, index) => (
                    <View key={item.label}>
                      <View className="flex-row justify-between">
                        <Text className="text-center text-base font-geologica-medium font-medium leading-[1.5] text-gray-700 py-3 flex-1">
                          {item.label}
                        </Text>
                        <Text className="text-center text-base font-geologica-regular font-normal leading-[1.5] text-gray-700 py-3 flex-1">
                          {item.totalCredits}
                        </Text>
                        <Text className="text-center text-base font-geologica-regular font-normal leading-[1.5] text-gray-700 py-3 flex-1">
                          {item.totalCredits} AED
                        </Text>
                      </View>
                      {index < pricingData.length - 1 && (
                        <View
                          className="h-[1px] bg-gray-200"
                          style={{ marginHorizontal: -12 }}
                        />
                      )}
                    </View>
                  ))}
                </View>
              </View>
            )}
            {/* Mentorship View */}
            {activeTab === "mentorship" && (
              <View className="">
                <View className="h-[28px]" />
                <View>
                  <Text className="text-base text-primary-0 font-geologica-regular font-normal leading-[1.5]">
                    Number of credits required per hour of tutoring
                  </Text>
                </View>
                <View className="h-[17px]" />
                <View className="p-3 bg-teachLoop-creamLight">
                  <View>
                    <View className="flex-row justify-between">
                      <Text className="text-sm font-geologica-regular font-normal leading-[1.5] text-gray-600 pt-3 pb-2 flex-1">
                        Level
                      </Text>
                    </View>
                    <View
                      className="h-[1px] bg-gray-200"
                      style={{ marginHorizontal: -12 }}
                    />
                  </View>
                  <View>
                    <View className="flex-row justify-between items-center">
                      <Text className="text-left text-base font-geologica-medium font-medium leading-[1.5] text-gray-700 py-3 flex-1">
                        Academic Support
                      </Text>
                      <Pressable onPress={contactSupport} className="flex-1">
                        <Text className="text-right text-base font-geologica-regular font-normal leading-[1.5] text-blue-600 underline py-6">
                          Contact us
                        </Text>
                      </Pressable>
                    </View>
                    <View
                      className="h-[1px] bg-gray-200"
                      style={{ marginHorizontal: -12 }}
                    />
                  </View>
                  <View>
                    <View className="flex-row justify-between items-center">
                      <Text className="text-left text-base font-geologica-medium font-medium leading-[1.5] text-gray-700 py-3 flex-1">
                        Wellbeing Support
                      </Text>
                      <Pressable onPress={contactSupport} className="flex-1">
                        <Text className="text-right text-base font-geologica-regular font-normal leading-[1.5] text-blue-600 underline py-6">
                          Contact us
                        </Text>
                      </Pressable>
                    </View>
                    <View
                      className="h-[1px] bg-gray-200"
                      style={{ marginHorizontal: -12 }}
                    />
                  </View>
                  <View>
                    <View className="flex-row justify-between items-center">
                      <Text className="text-left text-base font-geologica-medium font-medium leading-[1.5] text-gray-700 py-3 flex-1">
                        Parent Support
                      </Text>
                      <Text className="text-right text-base font-geologica-regular font-normal leading-[1.5] text-gray-700 py-6 flex-1">
                        Coming soon
                      </Text>
                    </View>
                    <View
                      className="h-[1px] bg-gray-200"
                      style={{ marginHorizontal: -12 }}
                    />
                  </View>
                  <View>
                    <View className="flex-row justify-between items-center">
                      <View className="flex-1">
                        <Text className="text-left text-base font-geologica-medium font-medium leading-1 text-gray-700 py-3 flex-1">
                          Group session
                        </Text>
                        <Text className="text-left text-sm font-geologica-regular font-normal leading-1">
                          (max 8 students)
                        </Text>
                      </View>
                      <Text className="text-right text-base font-geologica-regular font-normal leading-[1.5] text-gray-700 py-6 flex-1">
                        Coming soon
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </View>
          <View className="h-8" />
        </View>
      </BottomSheetView>
    </BottomSheet>
  ) : null;
}

export default LessonPriceSheet;
